import {
    CreativeRotationMethodMapping,
    getFlexibleFrequency,
} from 'states/resources/ads/business-logic';

export function hasChangedBillableImpressions(entry) {
    if (entry.patch.billable_volume !== undefined && entry.snapshot.ef_billing_terms === 'CPM') {
        return true;
    }

    return false;
}

export function hasChangedNonBillableImpressions(entry) {
    if (
        entry.patch.non_billable_volume !== undefined &&
        entry.snapshot.ef_billing_terms === 'CPM'
    ) {
        return true;
    }

    return false;
}

/**
 * Whether a flattened history patch key belongs to creative or rotation rules.
 *
 * Used to ignore unrelated patch keys (for example `start` or `automatic_bid_price.max_ecpm`)
 * when deciding if a history entry only changed creative weights.
 *
 * @param {string} key - Dot-notation patch key from a history entry
 * @returns {boolean} True when the key is `creative`, `creative.*`, `rotation_rules`, or `rotation_rules.*`
 *
 * @example
 * isCreativeRelatedPatchKey('creative.0'); // true
 * isCreativeRelatedPatchKey('rotation_rules.weighted.2.weight'); // true
 * isCreativeRelatedPatchKey('start'); // false
 * isCreativeRelatedPatchKey('automatic_bid_price.max_ecpm'); // false
 */
function isCreativeRelatedPatchKey(key) {
    return (
        key === 'creative' ||
        key.startsWith('creative.') ||
        key === 'rotation_rules' ||
        key.startsWith('rotation_rules.')
    );
}

/**
 * Whether creative-related patch changes are weight-only background updates.
 *
 * Returns true when the only creative/rotation_rules changes are `weighted.N.weight` or
 * `scheduled.N.weight` keys. For optimized rotation modes, callers use this to hide a ghost
 * "Creative" section when start date or bid price changed in the same request.
 *
 * Does not apply to weighted or scheduled rotation modes, where weight edits are user-facing.
 *
 * @param {Object<string, { value: *, op: string }>} patch - Flattened history patch for one entry
 * @param {{ rotation_rules?: { mode: string } }} snapshot - Snapshot after the change
 * @returns {boolean} True when changes are weight-only background updates; false when the user
 *   changed creatives, rotation mode, markup ids, or there are no creative-related patch keys
 *
 * @example
 * // Start date + background weight recalculation — suppress Creative in history UI
 * checkOnlyCreativeWeightChanges(
 *   {
 *     start: { value: '2025-11-12T15:26:43.000Z', op: 'replace' },
 *     'rotation_rules.weighted.0.weight': { value: 1, op: 'replace' },
 *     'rotation_rules.weighted.1.weight': { value: 1, op: 'replace' },
 *   },
 *   { rotation_rules: { mode: 'optimizedForClicks' } }
 * ); // true
 *
 * @example
 * // User replaced a creative markup id — show Creative in history UI
 * checkOnlyCreativeWeightChanges(
 *   { 'rotation_rules.weighted.0': { value: { markup_id: 'NEWID' }, op: 'replace' } },
 *   { rotation_rules: { mode: 'optimizedForClicks' } }
 * ); // false
 *
 * @example
 * // Weighted rotation mode — weight edits are always user-facing
 * checkOnlyCreativeWeightChanges(
 *   { 'rotation_rules.weighted.0.weight': { value: 50, op: 'replace' } },
 *   { rotation_rules: { mode: 'weighted' } }
 * ); // false
 */
export function checkOnlyCreativeWeightChanges(patch, snapshot) {
    const testWeightedOrScheduled = new RegExp('.*(weighted|scheduled).\\d.weight');
    const creativePatchKeys = Object.keys(patch).filter(isCreativeRelatedPatchKey);
    const nonWeightCreativePatchKeys = creativePatchKeys.filter(
        entry => !testWeightedOrScheduled.test(entry)
    );
    return (
        creativePatchKeys.length > 0 &&
        nonWeightCreativePatchKeys.length === 0 &&
        snapshot.rotation_rules &&
        snapshot.rotation_rules.mode !== CreativeRotationMethodMapping.Weighted &&
        snapshot.rotation_rules.mode !== CreativeRotationMethodMapping.Scheduled
    );
}

const CUSTOM_FIELD_HISTORY_PROPERTIES = ['name', 'key', 'value', 'required'];

/**
 * Whether a patch key belongs to a custom field index (any sub-property).
 *
 * @param {string} key - Flattened patch key
 * @param {number|string} customFieldIndex - Index in the ad's custom_fields array
 * @returns {boolean}
 *
 * @example
 * isCustomFieldPatchKey('custom_fields.0.required', 0); // true
 * isCustomFieldPatchKey('custom_fields.0.value', 0); // true
 * isCustomFieldPatchKey('custom_fields.1.name', 0); // false
 */
export function isCustomFieldPatchKey(key, customFieldIndex) {
    const index = String(customFieldIndex);
    if (key === 'custom_fields') {
        return true;
    }
    return key === `custom_fields.${index}` || key.startsWith(`custom_fields.${index}.`);
}

/**
 * Whether the patch touches a custom field at the given index.
 *
 * @param {Object<string, { value: *, op: string }>} patch
 * @param {number|string} customFieldIndex
 * @returns {boolean}
 */
export function hasCustomFieldPatchChange(patch, customFieldIndex) {
    if (patch?.custom_fields?.op === 'add') {
        return true;
    }
    return Object.keys(patch || {}).some(key => isCustomFieldPatchKey(key, customFieldIndex));
}

/**
 * @param {Object} snapshot
 * @param {{ name?: string, key?: string }} fieldTemplate
 * @param {number|string} [customFieldIndex]
 * @returns {Object|null}
 */
export function getCustomFieldFromSnapshot(snapshot, fieldTemplate, customFieldIndex) {
    const fields = snapshot?.custom_fields;
    if (!Array.isArray(fields) || fields.length === 0) {
        return null;
    }

    const { name, key } = fieldTemplate || {};

    if (customFieldIndex !== undefined && fields[customFieldIndex]) {
        const atIndex = fields[customFieldIndex];
        if (key && atIndex.key === key) {
            return atIndex;
        }
        if (name && atIndex.name === name) {
            return atIndex;
        }
        if (!key && !name) {
            return atIndex;
        }
    }

    if (key) {
        const byKey = fields.find(f => f.key === key);
        if (byKey) {
            return byKey;
        }
    }

    if (name) {
        const byName = fields.find(f => f.name === name);
        if (byName) {
            return byName;
        }
    }

    return fields[customFieldIndex] ?? null;
}

/**
 * Builds a custom field object from flattened history patch keys when the snapshot
 * does not contain `custom_fields` (or the field cannot be resolved from it).
 *
 * Merges, in order: the ad/campaign field template, per-property patch entries
 * (`custom_fields.N.value`, `.name`, `.required`, `.key`), a whole-field replace
 * (`custom_fields.N`), or a full-array replace (`custom_fields`).
 *
 * @param {Object<string, { value: *, op: string }>} patch - Flattened history patch
 * @param {number|string} customFieldIndex - Index in the `custom_fields` array
 * @param {{ name?: string, key?: string, value?: *, required?: boolean }} fieldTemplate
 *   - Current ad custom field definition used to fill missing properties
 * @returns {Object|null} Reconstructed field, or null when patch has no usable data
 *
 * @example
 * applyPatchToCustomFieldTemplate(
 *   { 'custom_fields.0.required': { value: true, op: 'replace' } },
 *   0,
 *   { name: 'Netsuite ID', key: 'netsuite_id', value: 'IO1', required: false }
 * );
 * // { name: 'Netsuite ID', key: 'netsuite_id', value: 'IO1', required: true }
 */
function applyPatchToCustomFieldTemplate(patch, customFieldIndex, fieldTemplate) {
    const field = {
        name: fieldTemplate?.name,
        key: fieldTemplate?.key,
        value: fieldTemplate?.value,
        required: fieldTemplate?.required,
    };
    let hasData = !!(fieldTemplate?.name || fieldTemplate?.key);

    CUSTOM_FIELD_HISTORY_PROPERTIES.forEach(prop => {
        const patchKey = `custom_fields.${customFieldIndex}.${prop}`;
        if (patch?.[patchKey] !== undefined) {
            field[prop] = patch[patchKey].value;
            hasData = true;
        }
    });

    const wholeFieldPatch = patch?.[`custom_fields.${customFieldIndex}`];
    if (wholeFieldPatch?.value && typeof wholeFieldPatch.value === 'object') {
        Object.assign(field, wholeFieldPatch.value);
        hasData = true;
    }

    const arrayPatch = patch?.custom_fields?.value;
    if (Array.isArray(arrayPatch) && arrayPatch[customFieldIndex]) {
        Object.assign(field, arrayPatch[customFieldIndex]);
        hasData = true;
    }

    return hasData ? field : null;
}

/**
 * Resolves a custom field for the current history entry (snapshot, then patch fallback).
 */
export function resolveCustomFieldForHistory(entry, customFieldIndex, fieldTemplate) {
    return (
        getCustomFieldFromSnapshot(entry.snapshot, fieldTemplate, customFieldIndex) ||
        applyPatchToCustomFieldTemplate(entry.patch, customFieldIndex, fieldTemplate)
    );
}

/**
 * Resolves a custom field for the previous history state on the selected entry.
 */
export function resolvePreviousCustomFieldForHistory(entry, customFieldIndex, fieldTemplate) {
    return (
        getCustomFieldFromSnapshot(entry._previousSnapshot, fieldTemplate, customFieldIndex) ||
        getCustomFieldFromSnapshot(entry.snapshot, fieldTemplate, customFieldIndex) ||
        applyPatchToCustomFieldTemplate(entry.patch, customFieldIndex, fieldTemplate)
    );
}

/**
 * Property names touched in the patch for a custom field index.
 */
export function getCustomFieldPropertiesInPatch(patch, customFieldIndex) {
    return CUSTOM_FIELD_HISTORY_PROPERTIES.filter(
        prop => patch?.[`custom_fields.${customFieldIndex}.${prop}`] !== undefined
    );
}

/**
 * Property names that changed between previous and current custom field state.
 * Falls back to patch keys when snapshots match but the patch still records a change.
 */
export function getCustomFieldChangedPropertyNames(
    previousField,
    currentField,
    patch,
    customFieldIndex
) {
    const fromSnapshots = CUSTOM_FIELD_HISTORY_PROPERTIES.filter(prop => {
        return previousField?.[prop] !== currentField?.[prop];
    });

    if (fromSnapshots.length > 0) {
        return fromSnapshots;
    }

    return getCustomFieldPropertiesInPatch(patch, customFieldIndex);
}

/**
 * Multi-line summary of custom field properties for history From/To columns.
 */
export function formatCustomFieldProperties(field) {
    if (!field) {
        return 'N/A';
    }

    return CUSTOM_FIELD_HISTORY_PROPERTIES.filter(prop => field[prop] !== undefined)
        .map(prop => `${prop}: ${field[prop]}`)
        .join('\n');
}

/**
 * Formats only the listed custom field properties (for per-property diffs).
 */
export function formatCustomFieldChangedProperties(field, propertyNames) {
    if (!propertyNames?.length) {
        return formatCustomFieldProperties(field);
    }

    if (!field) {
        return propertyNames.map(prop => `${prop}: N/A`).join('\n');
    }

    return propertyNames
        .map(prop => `${prop}: ${field[prop] !== undefined ? field[prop] : 'N/A'}`)
        .join('\n');
}

/**
 * Whether a history entry has custom field changes for the given index.
 *
 * Uses the patch as the source of truth. Snapshot pairs can be identical when metadata
 * was persisted in `properties` but normalized before the history snapshot was stored.
 *
 * @param {{ patch: Object }} entry
 * @param {number|string} customFieldIndex
 * @returns {boolean}
 */
export function hasCustomFieldChanged(entry, customFieldIndex) {
    return hasCustomFieldPatchChange(entry.patch, customFieldIndex);
}

/**
 * Formats frequency cap settings from an ad history snapshot for display.
 *
 * Uses `getFlexibleFrequency` when `fcaps` is present so history matches the ad
 * details page. Only falls back to `max_user_frequency` for snapshots that predate
 * the `fcaps` field entirely.
 *
 * @param {{ fcaps?: { imp: number|null, interval_count?: number, interval_unit?: string }, max_user_frequency?: number }|null} snapshot
 * @returns {string} Human-readable frequency cap label
 */
export function formatFrequencyCapFromSnapshot(snapshot) {
    if (!snapshot) {
        return 'Unlimited';
    }

    if (snapshot.fcaps != null) {
        return getFlexibleFrequency(snapshot.fcaps);
    }

    if (snapshot.max_user_frequency) {
        return `${snapshot.max_user_frequency} Impressions`;
    }

    return 'Unlimited';
}

/**
 * Whether a history patch includes a user-visible frequency cap change.
 *
 * `fcaps.start` is an internal cap-window timestamp and must not surface as a
 * Frequency Cap update when the displayed cap is unchanged.
 *
 * @param {{ patch: Object<string, { value: *, op: string }>, snapshot: object, _previousSnapshot?: object|null }} entry - History entry
 * @returns {boolean} True when the formatted frequency cap label would differ
 *
 * @example
 * hasFrequencyCapVisibleChange({ patch: { 'fcaps.start': { op: 'replace' } }, snapshot: {} }); // false
 * hasFrequencyCapVisibleChange({ patch: { 'fcaps.imp': { op: 'replace' } }, snapshot: {}, _previousSnapshot: {} }); // depends on labels
 */
export function hasFrequencyCapVisibleChange(entry) {
    const patch = entry.patch || {};
    const patchKeys = Object.keys(patch);

    const touchesFrequencyCap = patchKeys.some(key => {
        if (key === 'max_user_frequency' || key === 'fcaps') {
            return true;
        }

        return key.startsWith('fcaps.');
    });

    if (!touchesFrequencyCap) {
        return false;
    }

    const onlyInternalFcapChange = patchKeys.every(
        key => key === 'fcaps.start' || key === '_etag' || key.startsWith('_updated')
    );

    if (onlyInternalFcapChange) {
        return false;
    }

    return (
        formatFrequencyCapFromSnapshot(entry.snapshot) !==
        formatFrequencyCapFromSnapshot(entry._previousSnapshot)
    );
}
